<template>
    <component
        :is="noLabelWrapper ? 'div' : 'label'"
        class="ds-radio text-ds-content-primary inline-grid cursor-pointer items-center gap-x-2"
        :class="[label ? 'grid-cols-[max-content,1fr]' : 'grid-cols-[max-content]', { 'ds-radio-disabled': disabled }]"
    >
        <input
            v-model="model"
            :id="inputId"
            class="fixed left-offscreen"
            :disabled
            :name
            type="radio"
            :value
        />
        <div class="faux-radio transition-[background-size,border-color,outline-color]" />
        <span
            v-if="label"
            class="row-span-2 text-content-md text-content-primary"
        >
            {{ label }}
        </span>
    </component>
</template>

<script setup lang="ts" generic="T">
import { computed, useTemplateRef } from 'vue';

withDefaults(
    defineProps<{
        label?: string;
        name?: HTMLInputElement['name'];
        value: T;
        noLabelWrapper?: boolean;
        inputId?: HTMLInputElement['id'];
        disabled?: boolean;
    }>(),
    {
        noLabelWrapper: false,
        disabled: false,
    },
);

const model = defineModel<T>();
const labelRef = useTemplateRef<HTMLElement>('label');
const oneLineHeight = computed(() => {
    if (labelRef.value) {
        return getComputedStyle(labelRef.value).lineHeight;
    }
    return '24px'; // default to 16px font-size times 1.5 line-height
});
</script>

<style scoped lang="scss">
.ds-radio {
    --one-lh: v-bind(oneLineHeight);

    @supports (height: 1lh) {
        --one-lh: 1lh;
    }

    grid-template-rows: var(--one-lh, 24px) auto;
}

.ds-radio-disabled {
    @apply pointer-events-none cursor-not-allowed text-content-disabled;
}

$dotRadius: round(12.5px * 0.5);

.faux-radio {
    border: 1px solid;
    @apply border-ds-primary-400;
    border-radius: 100%;
    display: inline-flex;
    height: 20px;
    width: 20px;
    outline: 4px solid transparent;
    background-position: center;
    background-size: 0 0;
    background-repeat: no-repeat;
    background-image: radial-gradient(
        circle at center,
        theme('colors.ds.gray.800') 0,
        theme('colors.ds.gray.800') $dotRadius,
        transparent $dotRadius
    );
}

label:hover .faux-radio {
    @apply border-ds-gray-600 outline-ds-primary-200;
}

input[type='radio']:checked + .faux-radio {
    @apply border-ds-gray-800;
    background-size: 100% 100%;
}

input[type='radio']:focus + .faux-radio {
    @apply border-ds-gray-600 outline-ds-gray-200;
}

input[type='radio']:active + .faux-radio {
    @apply border-ds-gray-600 outline-ds-gray-300;
}

input[type='radio']:disabled + .faux-radio {
    @apply border-ds-gray-200 bg-white;
}

input[type='radio']:disabled:checked + .faux-radio {
    @apply border-ds-gray-200 bg-white;
    background-image: radial-gradient(
        circle at center,
        theme('colors.ds.gray.200') 0,
        theme('colors.ds.gray.200') $dotRadius,
        transparent $dotRadius
    );
}
</style>
